<template>
    <modal-side-generic
    upBellowEntry
    buttonFullWidth
    showFooter
    :triggerClose="triggerClose"
    :title="t('userArea.definitions.secureAssociationEnrollment.validateCodeModal.title')"
    :buttonsFooterData="buttonsFooterData"
    @hideMe="emit('hideMe')">
        <div class="content-container" :class="{ 'android': getDeviceInUse === 'android'}">
            <span>{{t('userArea.definitions.secureAssociationEnrollment.validateCodeModal.insertCode')}}</span>
            <!-- OTP input -->
            <input-generic
            :applyAutoCompleteOneTime="getDeviceInUse === 'ios'"
            :params="validationCodeParams"
            :inputValue="validationCode"
            @inputPasteData="inputPasteDataHandler"
            @input="validateCodeInputHandler"/>
            <!-- Resend Note and button -->
            <div class="resend-code-container">
                <span>{{t('userArea.definitions.secureAssociationEnrollment.validateCodeModal.codeMessage')}}</span>
                <button-link
                internalClick
                :label="t('userArea.definitions.secureAssociationEnrollment.validateCodeModal.resendMessage')"
                :disabled="!resendActive"
                @clicked="[emit('requestNewToken'), startWatching()]"/>
            </div>
        </div>
    </modal-side-generic>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import ButtonLink from '@/components/generic/buttons/Link'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Assets */
import { ref, onBeforeUnmount, onMounted } from 'vue'
import { noSpecialCharNoSpace } from '@/assets/scripts/validators'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getDeviceInUse } = mapGetters()

/* Emmitters */
const emit = defineEmits(['hideMe', 'validateCode', 'requestNewToken'])

/** props received
@enrollmentOtpTemplate { String }
*/
const props = defineProps({
	enrollmentOtpTemplate: String
})

// Generic Data
const validationCode = ref('')
const triggerClose = ref(false)
const resendActive = ref(false)
const countingToResend = ref(60)
const timeoutHolder = ref(null)
const watchingTimeouted = ref(false)

const validationCodeParams = ref({
    maxLength: 30,
    errorState: false,
    errorMessage: t('mandatoryFieldError'),
    ourMask: {
		validatorFn: value => {
            return noSpecialCharNoSpace(value)
        }
    },
    pasteParser: $event => {
        if (window.cordova?.platformId === 'ios') {
            const messageSplit = $event.split('#')
            return messageSplit[1].trim()
        } else {
            const messageSplit = $event.split(': ')[1].split(' ')
            return messageSplit[0].trim()
        }
    }
})

const inputPasteDataHandler = ($event) => {
    setTimeout(() => {
        validationCode.value = $event
    })
}

const validateCodeInputHandler = $event => {
    if ($event.length === 1) {
        validationCode.value = ''
    }
    validationCode.value = $event
    validationCodeParams.value.errorState = false
}

const buttonsFooterData = [{
    label: t('userArea.definitions.secureAssociationEnrollment.validateCodeModal.validate'),
    icon: 'check-line',
    iconPosition: 'right',
    buttonType: 'primary',
    callbackFunction: () => { validateCodeHandler() }
}]

const validateCodeHandler = () => {
    if (!validationCode.value.length) {
        validationCodeParams.value.errorState = true
        validationCodeParams.value.errorMessage = t('mandatoryFieldError')
        return
    }

    emit('validateCode', validationCode.value)
}

/*************************************************
 *                                                *
 *                Resend Token Logic              *
 *                                                *
 *************************************************/
const startResendTimer = () => {
    if (!timeoutHolder.value) {
        resendActive.value = false
        timeoutHolder.value = setTimeout(() => {
            timeoutHolder.value = undefined
            resendActive.value = true
        }, countingToResend.value * 1000) // 60 seconds until resend button activation
    }
}

const startWatching = () => {
    watchingTimeouted.value = false
    if (window.cordova?.platformId === 'android' && !watchingTimeouted.value) {
        window.cordova.plugins.smsRetriever.startWatching(successCallbackSMSRetriever, errorCallbackSMSRetriever, true)
    }
}

const successCallbackSMSRetriever = data => {
    console.log(data)
    if ((process.env.VUE_APP_ENV !== 'DEV' && data.SenderAddress && data.SenderAddress !== 'Banco BAI') ||
        (process.env.VUE_APP_ENV === 'DEV' && data.SenderAddress && data.SenderAddress !== 'BAI DEV')) {
        invalidSenderError()
        return
    }
    const message = data.Message
    const helper = props.enrollmentOtpTemplate.split('%')

    validationCode.value = message.replace(helper[0], '').replace(helper[2], '')
}

const errorCallbackSMSRetriever = error => {
    watchingTimeouted.value = true
    console.log(error)
}

/* Invalid Sender Error Toast */
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const invalidSenderError = () => {
    feedbackToastData.value = {
        toastType: 'negative',
        text: t('userArea.definitions.secureAssociationEnrollment.validateCodeModal.invalidSender'),
        buttons: [{
            text: t('commonButtons.ignore'),
            callbackAction: () => { showFeedbackToast.value = false }
        }]
    }

    showFeedbackToast.value = true
}

onMounted(() => {
    startResendTimer()
    startWatching()
})

onBeforeUnmount(() => {
    clearTimeout(timeoutHolder.value)
})

</script>

<style lang="scss" scoped>
.content-container {
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 24px;

    span {
        max-width: 312px;

        @include H7-Regular;
        color: var(--secondary-dark-1);
    }

    .resend-code-container {
        width: 100%;
        padding-top: 24px;

        display: flex;
        flex-direction: column;
        gap: 12px;

        border-top: 1px solid var(--secondary-light-2);

        span {
            max-width: 312px;

            @include H7-Regular;
            color: var(--secondary-dark-1);
        }

        .wrapper :deep(.button-link) {
            width: fit-content;
        }
    }

    &.android {
        .input-elements-container {
            pointer-events: none;
        }
    }
}
</style>
